/* eslint-disable @typescript-eslint/no-explicit-any */
import { Instant } from 'js-joda';

import * as Service from 'types/service';
import { AerialOverlays } from 'constants/service/floorViewConstants';
import { DEFAULT_ROOM_ID } from 'constants/service/roomConstants';
import { EventAction } from 'utils/analytics/amplitudeEventConstants';
import { PartyListSections } from 'constants/service/singlePartyList';
import { ReservationRightSidePanels } from 'constants/service/reservationPanels';
import { amplitudeSendEvent } from 'utils/analytics/amplitude';
import { assign, concat, isEmpty, without } from 'es-toolkit/compat';
import { core } from '@tock/coretypes';
import { dashboard } from 'types';
import { getUIState, saveUIState } from 'utils/service/localStorageUtils';
import PartyListTabs from 'constants/service/partyListTabs';
import PartyPanelProfileTabs from 'constants/service/partyPanelProfileTabs';
import ProfileTabNames from 'constants/ProfileTabNames';
import SidebarTabConstants from 'constants/service/SidebarTabConstants';
import TableGroupingOptions from 'constants/service/tableGroupingOptions';
import TableViewsConstants from 'constants/TableViewsConstants';

interface SaveableUIState {
  organizingTablesBy: string;
  recentlyResynced: boolean;
  selectedRoomId: number | null;
  selectedView: Service.FloorViewOption;
  visibleAerialOverlays: {
    [overlayName: string]: boolean;
  };
}

export type ServiceMode = 'DEFAULT' | 'RESCHEDULE';
export type ServicePartyDragMode = 'SEAT' | 'ASSIGN';
interface SelectedBusinessServerState {
  businessServerId: number;
  isAssigning: boolean;
}

export interface SelectedPartyListTabs {
  [PartyListTabs.waitlist]: boolean;
  [PartyListTabs.expected]: boolean;
  [PartyListTabs.seated]: boolean;
  [PartyListTabs.finished]: boolean;
  [PartyListTabs.walkIn]: boolean;
}

export interface ReservationRightSidePanelStatus {
  [ReservationRightSidePanels.TABLE_COMBINATIONS]: boolean;
  [ReservationRightSidePanels.RESERVATION_SETTINGS]: boolean;
}

export type ServiceUiModal = null | 'SendTextMessageModal' | 'ChaseTransferDialog' | 'NotifyModal';

interface State extends SaveableUIState {
  businessServerListNameFilter: string;
  canDragTables: boolean;
  creatingWalkInOfType: 'EXPECTED' | 'QUOTED' | null;
  editingWalkIn: boolean;
  failedToSaveAppState: false;
  isAssigningTables: boolean;
  latestExpectedReservationTime: null | string;
  latestQuotedTimeMin: string;
  loggedInPatron: null | core.Patron;
  mode: ServiceMode;
  newAssigningSession: boolean;
  partyDragMode: ServicePartyDragMode;
  partyListTextFilter: string;
  prevSelectedPartyId?: number;
  reservationRightSidePanels: ReservationRightSidePanelStatus;
  seatedWalkInId?: number;
  selectedBusinessServerState?: SelectedBusinessServerState;
  selectedPartyId?: number;
  selectedWaitlistId?: number;
  selectedSidebarTab: 'GuestsTab' | 'ServersTab';
  selectedGuestListSection: PartyListSections;
  selectedPartyListTabs: SelectedPartyListTabs;
  selectedPartyPanelProfileTab: 'QUICK' | 'NOTES' | 'TAGS';
  selectedProfileTab: 'booking' | 'guest' | 'timeline' | 'receipt' | 'check';
  selectedServer: null | dashboard.BusinessServer;
  selectedTableIds: number[];
  shouldResetBeforeUnload: boolean;
  showBusinessSwitcherModal: boolean;
  showProfile: boolean;
  showReleasesModal: boolean;
  showStatsPanel: boolean;
  showTableCombinationsPanel: boolean;
  visibleModal: ServiceUiModal;
  walkInSortByArrival: boolean;
}

const savedUI: SaveableUIState = getUIState() || {};
const initialOrganizingTablesBy = savedUI.organizingTablesBy || TableGroupingOptions.SECTION;
const initialRecentlyResynced = savedUI.recentlyResynced || false;
const initialSelectedRoomId = savedUI.selectedRoomId || DEFAULT_ROOM_ID;
const initialSelectedView = savedUI.selectedView || TableViewsConstants.AERIAL;
const initialVisibleAerialOverlays = savedUI.visibleAerialOverlays || {
  [AerialOverlays.preassignments]: true,
  [AerialOverlays.tableTimer]: true,
};

let selectedView = initialSelectedView;
let selectedViewAtInstant = Instant.now();
const visibleOverlayOnAtInstants: {
  [overlay: string]: Instant | null;
} = {
  [AerialOverlays.preassignments]: Instant.now(),
  [AerialOverlays.tableTimer]: Instant.now(),
};

export const initialState: State = {
  selectedBusinessServerState: undefined,
  selectedSidebarTab: SidebarTabConstants.GuestsTab,
  businessServerListNameFilter: '',
  canDragTables: false,
  creatingWalkInOfType: null,
  editingWalkIn: false,
  failedToSaveAppState: false,
  latestExpectedReservationTime: null,
  latestQuotedTimeMin: '30',
  loggedInPatron: null,
  isAssigningTables: false,
  mode: 'DEFAULT',
  newAssigningSession: false,
  organizingTablesBy: initialOrganizingTablesBy,
  partyDragMode: 'SEAT',
  partyListTextFilter: '',
  prevSelectedPartyId: undefined,
  recentlyResynced: initialRecentlyResynced,
  reservationRightSidePanels: {
    [ReservationRightSidePanels.TABLE_COMBINATIONS]: false,
    [ReservationRightSidePanels.RESERVATION_SETTINGS]: false,
  },
  selectedGuestListSection: PartyListSections.UPCOMING,
  selectedPartyId: undefined,
  selectedPartyListTabs: {
    [PartyListTabs.waitlist]: false,
    [PartyListTabs.walkIn]: false,
    [PartyListTabs.expected]: true,
    [PartyListTabs.seated]: false,
    [PartyListTabs.finished]: false,
  },
  selectedPartyPanelProfileTab: PartyPanelProfileTabs.NOTES,
  selectedProfileTab: ProfileTabNames.booking,
  selectedRoomId: initialSelectedRoomId,
  selectedServer: null,
  selectedTableIds: [],
  selectedView: initialSelectedView,
  shouldResetBeforeUnload: false,
  showProfile: false,
  showStatsPanel: false,
  visibleAerialOverlays: initialVisibleAerialOverlays,
  visibleModal: null,
  walkInSortByArrival: true,
  showBusinessSwitcherModal: false,
  showReleasesModal: false,
  showTableCombinationsPanel: false,
};

export default function ui(state = initialState, action: BaseTockAction): typeof initialState {
  const { type, payload } = action;

  switch (type) {
    case 'APP_STATE_LOCAL_STORAGE_SAVE_FAILED':
      return {
        ...state,
        failedToSaveAppState: payload,
      };

    case 'CLOSE_GLOBAL_MODAL':
      return {
        ...state,
        visibleModal: null,
      };

    case 'CLOSE_PROFILE_MODAL': {
      const update: any = {
        showProfile: false,
        selectedProfileTab: ProfileTabNames.booking,
      };
      // YK 2019/04/23 anyone know where this is from?
      // if (state.selectedTableIds) {
      //   update.selectedPartyId = null;
      // }
      return { ...state, ...update };
    }

    case 'CLOSE_WALK_IN_PANEL':
      return {
        ...state,
        selectedTableIds: [],
        creatingWalkInOfType: null,
        selectedPartyId: undefined,
        editingWalkIn: false,
      };

    case 'DESELECT_TABLE':
      return {
        ...state,
        selectedTableIds: without(state.selectedTableIds, payload),
      };

    case 'DESELECT_TABLES':
      // guard against this being called blindly
      if (isEmpty(state.selectedTableIds)) {
        return state;
      }
      return {
        ...state,
        selectedTableIds: [],
      };

    case 'REPLACE_TABLE':
      return {
        ...state,
        selectedTableIds: [payload],
      };

    case 'DESELECT_PARTY':
      // guard against blind calls
      if (!state.selectedPartyId && !state.isAssigningTables && !state.showProfile) {
        return state;
      }
      return {
        ...state,
        isAssigningTables: false,
        prevSelectedPartyId: state.selectedPartyId,
        selectedPartyId: undefined,
        showProfile: false,
      };

    case 'DESELECT_SERVER':
      return {
        ...state,
        selectedServer: null,
      };

    case 'OPEN_GLOBAL_MODAL':
      return {
        ...state,
        visibleModal: payload,
      };

    case 'OPEN_PROFILE_MODAL':
      return {
        ...state,
        showProfile: true,
        visibleModal: null,
      };

    case 'OPEN_WALK_IN_PANEL':
      return {
        ...state,
        creatingWalkInOfType: payload,
        showProfile: false,
      };

    case 'OPEN_WALK_IN_PANEL_FOR_EDITING':
      return {
        ...state,
        creatingWalkInOfType: payload.type,
        editingWalkIn: true,
        isAssigningTables: false,
      };

    case 'SELECT_PARTY':
      return {
        ...state,
        prevSelectedPartyId: state.selectedPartyId,
        selectedPartyId: payload,
        selectedTableIds: [],
      };

    case 'SELECT_PARTY_AND_VIEW_PROFILE':
      return {
        ...state,
        prevSelectedPartyId: state.selectedPartyId,
        selectedPartyId: payload,
        showProfile: true,
        visibleModal: null,
      };

    case 'SELECT_GUEST_LIST_SECTION': {
      return {
        ...state,
        selectedGuestListSection: payload,
      };
    }

    case 'SELECT_PARTY_LIST_TAB': {
      const nextSelectedPartyListTabs = assign(
        {},
        {
          [PartyListTabs.waitlist]: false,
          [PartyListTabs.expected]: false,
          [PartyListTabs.seated]: false,
          [PartyListTabs.finished]: false,
          [PartyListTabs.walkIn]: false,
        },
        {
          [payload]: true,
        }
      );
      return {
        ...state,
        selectedPartyListTabs: nextSelectedPartyListTabs,
        prevSelectedPartyId: undefined,
      };
    }
    case 'SELECT_PARTY_PANEL_PROFILE_TAB':
      return {
        ...state,
        selectedPartyPanelProfileTab: payload,
      };

    case 'SELECT_ROOM': {
      const newState = {
        ...state,
        selectedRoomId: payload,
      };
      saveUIStateHelper(newState);
      return newState;
    }

    case 'SELECT_VIEW': {
      trackHowLongViewWasSelected();
      selectedView = payload;
      selectedViewAtInstant = Instant.now();

      const newState = {
        ...state,
        selectedView,
      };
      saveUIStateHelper(newState);
      return newState;
    }

    case 'SELECT_SERVER':
      return {
        ...state,
        selectedServer: payload,
      };

    case 'SELECT_TABLE':
      return {
        ...state,
        selectedTableIds: concat(state.selectedTableIds, [payload]),
        selectedBusinessServerState: undefined,
      };

    case 'SELECT_WAITLIST_PARTY': {
      return {
        ...state,
        selectedWaitlistId: payload,
      };
    }

    case 'SET_LATEST_EXPECTED_RESERVATION_TIME': {
      return {
        ...state,
        latestExpectedReservationTime: payload,
      };
    }

    case 'SET_LATEST_QUOTED_TIME': {
      return {
        ...state,
        latestQuotedTimeMin: payload,
      };
    }

    // This happens in dashboard. Eventually, we can make the reducers listen
    // to where dashboard stores the logged in patron.
    // -- YK (2017-10-25)
    case 'PATRON_FETCH_PROFILE_DONE':
    case 'SET_LOGGED_IN_PATRON':
      return {
        ...state,
        loggedInPatron: payload,
      };

    case 'SET_SHOULD_RESET_BEFORE_UNLOAD': {
      return {
        ...state,
        shouldResetBeforeUnload: payload,
      };
    }
    case 'SET_RECENTLY_RESYNCED': {
      const newState = {
        ...state,
        recentlyResynced: payload,
      };
      saveUIStateHelper(newState);
      return newState;
    }
    case 'SET_PARTY_LIST_TEXT_FILTER':
      return {
        ...state,
        partyListTextFilter: payload,
      };

    case 'SET_ORGANIZING_TABLES_BY': {
      const newState = {
        ...state,
        organizingTablesBy: payload,
      };
      saveUIStateHelper(newState);
      return newState;
    }

    case 'SET_AERIAL_OVERLAY_VISIBILITY': {
      trackHowLongVisibleAerialOverlayWasOn(payload.overlay);
      visibleOverlayOnAtInstants[payload.overlay] = payload.visibility ? Instant.now() : null;
      const newState = {
        ...state,
        visibleAerialOverlays: {
          ...state.visibleAerialOverlays,
          [payload.overlay]: payload.visibility,
        },
      };
      saveUIStateHelper(newState);
      return newState;
    }

    case 'SET_CAN_DRAG_TABLES': {
      return {
        ...state,
        canDragTables: payload,
      };
    }

    case 'SET_STATUS_PANEL_IS_OPEN': {
      return {
        ...state,
        showStatsPanel: payload,
      };
    }

    case 'TOGGLE_IS_ASSIGNING_TABLES':
      return {
        ...state,
        isAssigningTables: !state.isAssigningTables,
      };

    case 'TOGGLE_NEW_ASSIGNING_SESSION':
      return {
        ...state,
        newAssigningSession: !state.newAssigningSession,
      };

    case 'TOGGLE_SORT_WALKINS_BY_ARRIVAL': {
      return {
        ...state,
        walkInSortByArrival: !state.walkInSortByArrival,
      };
    }

    case 'TOGGLE_TABLE_COMBINATIONS_PANEL': {
      return {
        ...state,
        showTableCombinationsPanel: !state.showTableCombinationsPanel,
      };
    }

    case 'TOGGLE_RESERVATION_RIGHT_SIDE_PANEL': {
      const current = state.reservationRightSidePanels[payload as ReservationRightSidePanels];
      return {
        ...state,
        reservationRightSidePanels: {
          ...initialState.reservationRightSidePanels,
          [payload]: !current,
        },
      };
    }

    case 'RESET_RESERVATION_RIGHT_SIDE_PANEL': {
      return {
        ...state,
        reservationRightSidePanels: {
          ...initialState.reservationRightSidePanels,
        },
      };
    }

    case 'UI_SELECT_SIDEBAR_TAB': {
      // guard against setting the same value if called
      // blindly
      if (state.selectedSidebarTab === payload) {
        return state;
      }
      return {
        ...state,
        selectedSidebarTab: payload,
        businessServerListNameFilter: '',
        partyListTextFilter: '',
      };
    }

    case 'SET_BUSINESS_SERVER_LIST_NAME_FILTER': {
      return {
        ...state,
        businessServerListNameFilter: payload,
      };
    }

    case 'SERVICE_SELECT_SERVER': {
      return {
        ...state,
        selectedBusinessServerState: { businessServerId: payload, isAssigning: false },
      };
    }

    case 'SERVICE_CLEAR_SERVER': {
      return {
        ...state,
        selectedBusinessServerState: undefined,
      };
    }

    case 'START_ASSIGNING_BUSINESS_SERVER': {
      return {
        ...state,
        selectedBusinessServerState: { businessServerId: payload, isAssigning: true },
      };
    }

    case 'STOP_ASSIGNING_BUSINESS_SERVER': {
      let newSelectedBusinessServerState;
      if (state.selectedBusinessServerState) {
        newSelectedBusinessServerState = {
          businessServerId: state.selectedBusinessServerState.businessServerId,
          isAssigning: false,
        };
      }
      return {
        ...state,
        selectedBusinessServerState: newSelectedBusinessServerState,
      };
    }

    case 'SERVICE_SET_MODE': {
      return {
        ...state,
        mode: payload,
      };
    }

    case 'SERVICE_SET_DRAG_MODE': {
      if (payload === 'SEAT') {
        return {
          ...state,
          partyDragMode: 'SEAT',
        };
      } else {
        return {
          ...state,
          partyDragMode: payload,
        };
      }
    }

    case 'SET_SEATED_WALKIN_ID': {
      return {
        ...state,
        seatedWalkInId: payload,
      };
    }

    default:
      return state;
  }
}

function saveUIStateHelper(newState: SaveableUIState) {
  saveUIState({
    organizingTablesBy: newState.organizingTablesBy,
    recentlyResynced: newState.recentlyResynced,
    selectedRoomId: newState.selectedRoomId,
    selectedView: newState.selectedView,
    visibleAerialOverlays: newState.visibleAerialOverlays,
  });
}

function trackHowLongViewWasSelected() {
  // Log how long the view was selected for
  const timeViewWasSelected = Instant.now().toEpochMilli() - selectedViewAtInstant.toEpochMilli();
  amplitudeSendEvent(EventAction.TIMING_SERVICE_VIEW_SELECTED, {
    timingVar: selectedView,
    timingValue: timeViewWasSelected,
  });
}

function trackHowLongVisibleAerialOverlayWasOn(overlayName: any) {
  const visibleAtInstant = visibleOverlayOnAtInstants[overlayName];
  if (!visibleAtInstant) {
    return;
  }
  const timeOverlayWasOn = Instant.now().toEpochMilli() - visibleAtInstant.toEpochMilli();
  amplitudeSendEvent(EventAction.TIMING_SERVICE_AERIAL_OVERLAY_ON, {
    timingVar: overlayName,
    timingValue: timeOverlayWasOn,
  });
}
